import { useEffect, useRef, useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMapsLibrary } from '@vis.gl/react-google-maps'
import { useOrg } from '@/context/OrgContext'
import { useCreateBuilding, useUpdateBuilding } from '@/hooks'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { GPSMap } from '@/components'
import type { Building } from '@/types'

const buildingFormSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name is too long'),
  address: z.string().max(255, 'Address is too long').optional(),
  gps_radius_metres: z.number().int().min(10).max(10_000),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  gps_center_latitude: z.number().optional(),
  gps_center_longitude: z.number().optional(),
})

type BuildingFormValues = z.infer<typeof buildingFormSchema>

interface BuildingFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  building?: Building
}

export function BuildingFormDialog({
  open,
  onOpenChange,
  building,
}: BuildingFormDialogProps) {
  const { effectiveOrgId } = useOrg()
  const isEditing = !!building
  const placesLib = useMapsLibrary('places')

  const createMutation = useCreateBuilding(effectiveOrgId)
  const updateMutation = useUpdateBuilding(building?.id ?? '', effectiveOrgId)

  const form = useForm<BuildingFormValues>({
    resolver: zodResolver(buildingFormSchema),
    defaultValues: {
      name: building?.name ?? '',
      address: building?.address ?? '',
      gps_radius_metres: building?.gps_radius_metres ?? 150,
      latitude: building?.latitude,
      longitude: building?.longitude,
      gps_center_latitude: building?.gps_center_latitude,
      gps_center_longitude: building?.gps_center_longitude,
    },
  })

  useEffect(() => {
    if (open) {
      form.reset({
        name: building?.name ?? '',
        address: building?.address ?? '',
        gps_radius_metres: building?.gps_radius_metres ?? 150,
        latitude: building?.latitude,
        longitude: building?.longitude,
        gps_center_latitude: building?.gps_center_latitude,
        gps_center_longitude: building?.gps_center_longitude,
      })
    }
  }, [open, building, form])

  const autocompleteRef = useRef<google.maps.places.PlaceAutocompleteElement | null>(null)
  const [autocompleteContainerEl, setAutocompleteContainerEl] = useState<HTMLDivElement | null>(null)
  const formRef = useRef(form)
  useEffect(() => { formRef.current = form }, [form])

  useEffect(() => {
    if (!placesLib || !autocompleteContainerEl) return

    const element = new google.maps.places.PlaceAutocompleteElement({})
    element.setAttribute('placeholder', 'Start typing an address…')

    const currentAddress = formRef.current.getValues('address')
    if (currentAddress) {
      element.value = currentAddress
    }

    autocompleteContainerEl.appendChild(element)
    autocompleteRef.current = element

    const handleSelect = async (event: Event) => {
      const placeEvent = event as unknown as { placePrediction: google.maps.places.PlacePrediction }
      const place = placeEvent.placePrediction?.toPlace()
      if (!place) return
      await place.fetchFields({ fields: ['location', 'formattedAddress'] })

      const lat = place.location?.lat()
      const lng = place.location?.lng()

      if (lat == null || lng == null) return

      formRef.current.setValue('address', place.formattedAddress ?? '')
      formRef.current.setValue('latitude', lat)
      formRef.current.setValue('longitude', lng)
      const existingCenterLat = formRef.current.getValues('gps_center_latitude')
      const existingCenterLng = formRef.current.getValues('gps_center_longitude')
      if (existingCenterLat == null || existingCenterLng == null) {
        formRef.current.setValue('gps_center_latitude', lat)
        formRef.current.setValue('gps_center_longitude', lng)
      }
    }

    element.addEventListener('gmp-select', handleSelect)

    return () => {
      element.removeEventListener('gmp-select', handleSelect)
      if (autocompleteContainerEl.contains(element)) {
        autocompleteContainerEl.removeChild(element)
      }
      autocompleteRef.current = null
    }
  }, [placesLib, autocompleteContainerEl])

  const handleCenterChange = useCallback(
    (lat: number, lng: number) => {
      form.setValue('gps_center_latitude', lat)
      form.setValue('gps_center_longitude', lng)
    },
    [form],
  )

  const isPending = createMutation.isPending || updateMutation.isPending

  const lat = form.watch('latitude')
  const lng = form.watch('longitude')
  const centerLat = form.watch('gps_center_latitude')
  const centerLng = form.watch('gps_center_longitude')
  const radiusMetres = Number(form.watch('gps_radius_metres')) || 150
  const hasCoords = lat != null && lng != null

  function onSubmit(values: BuildingFormValues) {
    const data = {
      name: values.name,
      address: values.address || undefined,
      gps_radius_metres: values.gps_radius_metres,
      latitude: values.latitude,
      longitude: values.longitude,
      gps_center_latitude: values.gps_center_latitude,
      gps_center_longitude: values.gps_center_longitude,
    }
    if (isEditing) {
      updateMutation.mutate(data, { onSuccess: () => onOpenChange(false) })
    } else {
      createMutation.mutate(data, { onSuccess: () => onOpenChange(false) })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Building' : 'Add Building'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the building details below.'
              : 'Enter the details for the new building.'}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid gap-6 sm:grid-cols-2">
              {/* Left column — form fields */}
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Building name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="address"
                  render={() => (
                    <FormItem>
                      <FormLabel>Address</FormLabel>
                      <FormControl>
                        <div ref={setAutocompleteContainerEl} className="gmp-autocomplete-wrapper" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="gps_radius_metres"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>GPS Radius (metres)</FormLabel>
                      <FormControl>
                        <Input type="number" min={10} max={10000} {...field} onChange={(e) => field.onChange(e.target.valueAsNumber)} />
                      </FormControl>
                      <p className="text-xs text-muted-foreground">
                        Reports must be submitted within this distance of the validation center.
                        Typical: 50 m for a single floor, 300 m+ for a large campus.
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Right column — map preview */}
              <div className="space-y-2">
                <p className="text-sm font-medium">GPS Zone Preview</p>
                {hasCoords ? (
                  <>
                    <GPSMap
                      geocodedLat={lat}
                      geocodedLng={lng}
                      centerLat={centerLat}
                      centerLng={centerLng}
                      radiusMetres={radiusMetres}
                      onCenterChange={handleCenterChange}
                    />
                    <p className="text-xs text-muted-foreground">
                      <span className="inline-block w-3 h-3 rounded-full bg-gray-500 mr-1 align-middle" />
                      Building address ·{' '}
                      <span className="inline-block w-3 h-3 rounded-full bg-blue-500 mr-1 align-middle" />
                      Validation center (drag to reposition)
                    </p>
                  </>
                ) : (
                  <div className="flex items-center justify-center h-64 rounded-md border border-dashed text-sm text-muted-foreground bg-muted/30">
                    Select an address to preview the GPS zone
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? isEditing
                    ? 'Saving…'
                    : 'Creating…'
                  : isEditing
                    ? 'Save Changes'
                    : 'Add Building'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
